// Theme generator — the color math behind the Lex theme system.
//
// Slack's theming model (the architecture EdgeLex adopts): a theme is a tiny
// seed (accent + neutral temperature), everything else is DERIVED, and a
// contrast gate guarantees no seed can produce an illegible UI. This module is
// the derivation + gate. It deliberately mirrors CSS `color-mix(in oklab, …)`
// so TypeScript-side validation (tests, custom-seed checks) predicts exactly
// what lex-theme.css renders.
//
// Pure functions only — no DOM access — so the whole module is unit-testable.

import { contrastRatio, parseHexColor, type RGB } from './contrastRatio';

export interface OKLab {
  L: number;
  a: number;
  b: number;
}

export interface OKLCH {
  L: number;
  C: number;
  H: number; // degrees
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

const srgbChannelToLinear = (channel: number): number => {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

const linearChannelToSrgb = (linear: number): number => {
  const c = clamp01(linear);
  const srgb = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  return Math.round(clamp01(srgb) * 255);
};

export const rgbToHex = ({ r, g, b }: RGB): string =>
  `#${[r, g, b].map((c) => Math.min(255, Math.max(0, Math.round(c))).toString(16).padStart(2, '0')).join('')}`;

// Björn Ottosson's reference OKLab conversion (the same space CSS
// `color-mix(in oklab, …)` interpolates in).
export const rgbToOklab = ({ r, g, b }: RGB): OKLab => {
  const lr = srgbChannelToLinear(r);
  const lg = srgbChannelToLinear(g);
  const lb = srgbChannelToLinear(b);

  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);

  return {
    L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  };
};

export const oklabToRgb = ({ L, a, b }: OKLab): RGB => {
  const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
  const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
  const s = Math.pow(L - 0.0894841775 * a - 1.291485548 * b, 3);

  return {
    r: linearChannelToSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: linearChannelToSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: linearChannelToSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  };
};

export const oklabToOklch = ({ L, a, b }: OKLab): OKLCH => ({
  L,
  C: Math.sqrt(a * a + b * b),
  H: ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360,
});

export const oklchToOklab = ({ L, C, H }: OKLCH): OKLab => {
  const rad = (H * Math.PI) / 180;
  return { L, a: C * Math.cos(rad), b: C * Math.sin(rad) };
};

export const hexToOklch = (hex: string): OKLCH | null => {
  const rgb = parseHexColor(hex);
  return rgb ? oklabToOklch(rgbToOklab(rgb)) : null;
};

export const oklchToHex = (oklch: OKLCH): string => rgbToHex(oklabToRgb(oklchToOklab(oklch)));

/**
 * TypeScript mirror of CSS `color-mix(in oklab, a <weightA*100>%, b)`.
 * Returns null when either hex fails to parse.
 */
export const mixOklab = (hexA: string, weightA: number, hexB: string): string | null => {
  const rgbA = parseHexColor(hexA);
  const rgbB = parseHexColor(hexB);
  if (!rgbA || !rgbB) return null;
  const w = clamp01(weightA);
  const labA = rgbToOklab(rgbA);
  const labB = rgbToOklab(rgbB);
  return rgbToHex(
    oklabToRgb({
      L: labA.L * w + labB.L * (1 - w),
      a: labA.a * w + labB.a * (1 - w),
      b: labA.b * w + labB.b * (1 - w),
    }),
  );
};

export const contrastRatioBetweenHex = (fgHex: string, bgHex: string): number | null => {
  const fg = parseHexColor(fgHex);
  const bg = parseHexColor(bgHex);
  if (!fg || !bg) return null;
  return contrastRatio(fg, bg);
};

// The two text candidates the accent palette in lex-theme.css already uses:
// white for dark accents, near-black ink for light accents (amber/yellow/lime…).
export const ACCENT_CONTRAST_LIGHT = '#ffffff';
export const ACCENT_CONTRAST_DARK = '#111827';

/**
 * Choose the readable text color for content rendered ON an accent surface
 * (primary buttons, selected pills). Picks whichever of white/ink contrasts
 * more with the seed — fixes the standing gap where a custom light accent
 * (e.g. banana yellow) got hardcoded white text.
 */
export const pickAccentContrast = (seedHex: string): string => {
  const white = contrastRatioBetweenHex(ACCENT_CONTRAST_LIGHT, seedHex) ?? 0;
  const ink = contrastRatioBetweenHex(ACCENT_CONTRAST_DARK, seedHex) ?? 0;
  return white >= ink ? ACCENT_CONTRAST_LIGHT : ACCENT_CONTRAST_DARK;
};

export interface ContrastRepairResult {
  hex: string;
  ratio: number;
  adjusted: boolean;
}

/**
 * Contrast gate with auto-repair (the non-negotiable of the Slack model).
 * Given a fixed foreground and a candidate background, walk the background's
 * OKLCH lightness AWAY from the foreground's until the WCAG ratio is met.
 * Chroma is progressively reduced near the lightness extremes so repairs
 * converge instead of saturating out of gamut. Returns the original color
 * unchanged when it already passes.
 */
export const repairContrast = (
  fgHex: string,
  bgHex: string,
  minRatio: number,
): ContrastRepairResult | null => {
  const fg = parseHexColor(fgHex);
  const initial = hexToOklch(bgHex);
  if (!fg || !initial) return null;

  const startRatio = contrastRatioBetweenHex(fgHex, bgHex);
  if (startRatio === null) return null;
  if (startRatio >= minRatio) return { hex: bgHex, ratio: startRatio, adjusted: false };

  const fgLch = hexToOklch(fgHex);
  if (!fgLch) return null;
  // Push bg lightness away from the fg's lightness.
  const direction = fgLch.L >= initial.L ? -1 : 1;

  let current: OKLCH = { ...initial };
  let best: ContrastRepairResult = { hex: bgHex, ratio: startRatio, adjusted: false };
  for (let step = 0; step < 100; step += 1) {
    const nextL = clamp01(current.L + direction * 0.01);
    // Fade chroma toward the extremes so very light/dark repairs stay in gamut.
    const extremity = Math.max(0, Math.abs(nextL - 0.5) - 0.3) / 0.2;
    const nextC = current.C * (1 - 0.35 * clamp01(extremity));
    current = { L: nextL, C: nextC, H: current.H };

    const hex = oklchToHex(current);
    const ratio = contrastRatioBetweenHex(fgHex, hex);
    if (ratio !== null && ratio > best.ratio) {
      best = { hex, ratio, adjusted: true };
    }
    if (ratio !== null && ratio >= minRatio) {
      return { hex, ratio, adjusted: true };
    }
    if (nextL === 0 || nextL === 1) break;
  }
  return best;
};

// WCAG 2.1 thresholds used by the theme gate.
export const WCAG_AA_TEXT = 4.5;
export const WCAG_AA_UI = 3;

// Frame tint strengths (percent of accent mixed into the frame surfaces).
// Light mode stays subtle so the global dark text remains readable on the
// tinted frame; dark mode can take a stronger wash. Named themes may override.
export const DEFAULT_FRAME_TINT = { light: 8, dark: 14 } as const;

export interface FrameTint {
  light: number;
  dark: number;
}

/**
 * Predict the frame (header/sidebar) surface colors a theme produces, exactly
 * as lex-theme.css computes them:
 *   light: color-mix(in oklab, accent T%, neutral-50|100)
 *   dark:  color-mix(in oklab, accent T%, mode-mixed neutral-950/900 base)
 * `neutralRamp` is the active surface palette's 50→950 ramp.
 */
export const deriveFrameSurfaces = (
  seedHex: string,
  neutralRamp: Record<string, string>,
  tint: FrameTint,
): { light: { header: string | null; sidebar: string | null }; dark: { header: string | null; sidebar: string | null } } => {
  const darkHeaderBase = mixOklab(neutralRamp['950'], 0.5, neutralRamp['900']);
  const darkSidebarBase = mixOklab(neutralRamp['950'], 0.7, neutralRamp['900']);
  return {
    light: {
      header: mixOklab(seedHex, tint.light / 100, neutralRamp['50']),
      sidebar: mixOklab(seedHex, tint.light / 100, neutralRamp['100']),
    },
    dark: {
      header: darkHeaderBase ? mixOklab(seedHex, tint.dark / 100, darkHeaderBase) : null,
      sidebar: darkSidebarBase ? mixOklab(seedHex, tint.dark / 100, darkSidebarBase) : null,
    },
  };
};
